import type { PitchClass, Accidental, Clef, Note, NoteRangePreset } from '../types/music';
import { getAccidentalSymbol, getAccidentalShortSymbol } from './keySignatures';

/**
 * MIDI number for each pitch class in octave 0
 * C0 = 12, C4 (middle C) = 60
 */
const PITCH_CLASS_TO_MIDI_OFFSET: Record<PitchClass, number> = {
  'C': 0,
  'D': 2,
  'E': 4,
  'F': 5,
  'G': 7,
  'A': 9,
  'B': 11
};

/**
 * Calculate MIDI number from pitch class, accidental, and octave
 * Middle C (C4) = 60
 */
export function getMidiNumber(pitchClass: PitchClass, accidental: Accidental, octave: number): number {
  const baseNumber = 12 + (octave * 12) + PITCH_CLASS_TO_MIDI_OFFSET[pitchClass];

  switch (accidental) {
    case 'sharp': return baseNumber + 1;
    case 'flat': return baseNumber - 1;
    default: return baseNumber;
  }
}

/**
 * Staff position calculation
 *
 * For TREBLE clef (G clef):
 * - Middle line (line 3) = B4
 * - Each position is a step (line or space)
 * - C4 (middle C) is 1 ledger line below the staff = position -6
 *
 * For BASS clef (F clef):
 * - Middle line (line 3) = D3
 * - Each position is a step
 * - C3 is on the space above middle line = position 1
 * - Middle C (C4) is 1 ledger line above the staff = position 6
 */
export function calculateStaffPosition(pitchClass: PitchClass, octave: number, clef: Clef): number {
  // Position of each pitch class within an octave (0 = C)
  const pitchPosition: Record<PitchClass, number> = {
    'C': 0, 'D': 1, 'E': 2, 'F': 3, 'G': 4, 'A': 5, 'B': 6
  };

  if (clef === 'treble') {
    // B4 is at position 0 (middle line of treble staff)
    // C4 is at position -6 (middle C, one ledger line below)
    const referenceOctave = 4;
    const referencePitch = 6; // B
    const notePosition = pitchPosition[pitchClass] + (octave - referenceOctave) * 7;
    return notePosition - referencePitch;
  } else {
    // D3 is at position 0 (middle line of bass staff)
    // C3 is at position -1
    const referenceOctave = 3;
    const referencePitch = 1; // D
    const notePosition = pitchPosition[pitchClass] + (octave - referenceOctave) * 7;
    return notePosition - referencePitch;
  }
}

/**
 * Get the display name for a note
 * e.g., "Middle C (C4)", "F♯3", "B♭5"
 */
export function getNoteDisplayName(note: Note): string {
  const accidentalSymbol = getAccidentalSymbol(note.accidental);
  const baseName = `${note.pitchClass}${accidentalSymbol}${note.octave}`;

  // Add friendly names for common notes
  if (note.pitchClass === 'C' && note.octave === 4 && note.accidental === 'natural') {
    return `Middle C (${baseName})`;
  }

  return baseName;
}

/**
 * Get short display name (for compact displays)
 * e.g., "C4", "F#3", "Bb5"
 */
export function getNoteShortName(note: Note): string {
  const accidentalSymbol = getAccidentalShortSymbol(note.accidental);
  return `${note.pitchClass}${accidentalSymbol}${note.octave}`;
}

/**
 * Get the clef display name
 */
export function getClefDisplayName(clef: Clef): string {
  return clef === 'treble' ? 'Treble Clef' : 'Bass Clef';
}

/**
 * Parse a note string like "C4" or "F#3" into components
 */
export function parseNoteString(noteStr: string): { pitchClass: PitchClass; accidental: Accidental; octave: number } | null {
  const match = noteStr.match(/^([A-G])([#b]?)(\d)$/);
  if (!match) return null;

  const pitchClass = match[1] as PitchClass;
  const accidentalStr = match[2];
  const octave = parseInt(match[3], 10);

  let accidental: Accidental = 'natural';
  if (accidentalStr === '#') accidental = 'sharp';
  else if (accidentalStr === 'b') accidental = 'flat';

  return { pitchClass, accidental, octave };
}

/**
 * Compare two notes for equality (by pitch, ignoring clef)
 */
export function notesEqual(a: Note | null, b: Note | null): boolean {
  if (!a || !b) return false;
  return a.pitchClass === b.pitchClass &&
         a.accidental === b.accidental &&
         a.octave === b.octave;
}

/**
 * Check if a note input matches a note (for answer validation)
 */
export function inputMatchesNote(
  input: { pitchClass: PitchClass; accidental: Accidental },
  note: Note
): boolean {
  return input.pitchClass === note.pitchClass && input.accidental === note.accidental;
}

/**
 * Preset note ranges for treble clef
 */
export const TREBLE_RANGE_PRESETS: NoteRangePreset[] = [
  { id: 'easy', label: 'Easy (C4 - G5)', low: 'C4', high: 'G5' },
  { id: 'medium', label: 'Medium (B3 - A5)', low: 'B3', high: 'A5' },
  { id: 'hard', label: 'Hard (G3 - C6)', low: 'G3', high: 'C6' },
  { id: 'full', label: 'Full Range (E3 - C6)', low: 'E3', high: 'C6' }
];

/**
 * Preset note ranges for bass clef
 * Easy range: C3 to C4 (Middle C) - notes within and just around the staff
 * This is the "Bass C through Middle C" range specified in requirements
 */
export const BASS_RANGE_PRESETS: NoteRangePreset[] = [
  { id: 'easy', label: 'Easy (C3 - C4)', low: 'C3', high: 'C4' },
  { id: 'medium', label: 'Medium (G2 - D4)', low: 'G2', high: 'D4' },
  { id: 'hard', label: 'Hard (E2 - F4)', low: 'E2', high: 'F4' },
  { id: 'full', label: 'Full Range (C2 - G4)', low: 'C2', high: 'G4' }
];

/**
 * Get VexFlow note name for rendering
 * VexFlow uses format like "c/4" for C4, "f#/3" for F#3
 */
export function getVexFlowNoteName(note: Note): string {
  const pitchLower = note.pitchClass.toLowerCase();
  let accidentalStr = '';
  if (note.accidental === 'sharp') accidentalStr = '#';
  else if (note.accidental === 'flat') accidentalStr = 'b';

  return `${pitchLower}${accidentalStr}/${note.octave}`;
}

/**
 * Get VexFlow accidental type
 */
export function getVexFlowAccidental(accidental: Accidental): string | null {
  switch (accidental) {
    case 'sharp': return '#';
    case 'flat': return 'b';
    default: return null;
  }
}

/**
 * Color codes for pitch classes (used when color coding is enabled)
 */
export const PITCH_CLASS_COLORS: Record<PitchClass, string> = {
  'C': '#ef4444', // red
  'D': '#f97316', // orange
  'E': '#eab308', // yellow
  'F': '#22c55e', // green
  'G': '#06b6d4', // cyan
  'A': '#3b82f6', // blue
  'B': '#a855f7'  // purple
};

/**
 * Check if a MIDI number is within a note range
 */
export function isMidiInRange(midi: number, lowNote: string, highNote: string): boolean {
  const low = parseNoteString(lowNote);
  const high = parseNoteString(highNote);

  if (!low || !high) return false;

  const lowMidi = getMidiNumber(low.pitchClass, low.accidental, low.octave);
  const highMidi = getMidiNumber(high.pitchClass, high.accidental, high.octave);

  return midi >= lowMidi && midi <= highMidi;
}
